import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CheckCircle2, RotateCcw, DollarSign, Handshake, Loader2, Search, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 10/19 (Josh): co-funded deals — the partner (Milvado / Enod …) paid the broker, so every PFS and every DIB
 * position owes the partner its own (position %) × commission. DIB and PFS are SEPARATE payers (two accountings),
 * one line each per deal. Rows are computed live from the deal + positions; only the paid state is stored.
 */
export default function CofundedPayables() {
  const { getAuthHeaders } = useAuth();
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({});
  const [partners, setPartners] = useState([]);
  const [payersFound, setPayersFound] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('owed');
  const [payerFilter, setPayerFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [busyKey, setBusyKey] = useState(null);
  const [payEntry, setPayEntry] = useState(null);
  const [payDate, setPayDate] = useState('');
  const [payRef, setPayRef] = useState('');
  const [payNote, setPayNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter });
      if (partnerFilter !== 'all') params.set('partner_contact_id', partnerFilter);
      if (payerFilter !== 'all') params.set('payer', payerFilter);
      const res = await axios.get(`${API}/cofunded-payables?${params}`, { headers: getAuthHeaders() });
      setEntries(res.data.entries || []);
      setTotals(res.data.totals_by_partner || {});
      setPartners(res.data.partners || []);
      setPayersFound(res.data.payer_contacts_found || []);
    } catch (e) {
      toast.error(`Failed to load co-funded payables: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, statusFilter, partnerFilter, payerFilter]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => [e.deal_id_number, e.deal_name, e.partner_name, e.reference, e.note]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [entries, search]);

  const openPay = (entry) => {
    setPayEntry(entry);
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayRef('');
    setPayNote('');
  };

  const submitPay = async () => {
    if (!payEntry) return;
    setBusyKey(payEntry.key);
    try {
      await axios.put(`${API}/cofunded-payables/${payEntry.deal_id}/${payEntry.payer}/mark-paid`, {
        paid_date: payDate ? `${payDate}T00:00:00` : null,
        reference: payRef || null,
        note: payNote || null,
      }, { headers: getAuthHeaders() });
      toast.success(`${payEntry.payer} → ${payEntry.partner_name} marked paid — #${payEntry.deal_id_number} ${formatCurrency(payEntry.amount_owed)}`);
      setPayEntry(null);
      await load();
    } catch (e) {
      toast.error(`Mark paid failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyKey(null);
    }
  };

  const unmark = async (entry) => {
    setBusyKey(entry.key);
    try {
      await axios.put(`${API}/cofunded-payables/${entry.deal_id}/${entry.payer}/unmark-paid`, {}, { headers: getAuthHeaders() });
      toast.success(`Undone — #${entry.deal_id_number} ${entry.payer}`);
      await load();
    } catch (e) {
      toast.error(`Undo failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyKey(null);
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({ status: statusFilter });
      if (partnerFilter !== 'all') params.set('partner_contact_id', partnerFilter);
      const res = await axios.get(`${API}/cofunded-payables/export.csv?${params}`, { headers: getAuthHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cofunded_payables_${statusFilter}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Export failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const missingPayers = ['PFS', 'DIB'].filter((p) => !payersFound.includes(p));

  return (
    <div className="space-y-6" data-testid="cofunded-payables">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          Co-funded deals: the partner paid the broker, so each PFS and each DIB position owes the partner its own % of the commission.
          PFS and DIB are separate payers — one line each per deal.
        </p>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input className="pl-8 w-52" placeholder="Search deal, merchant, ref…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={partnerFilter} onValueChange={setPartnerFilter}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Partner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All partners</SelectItem>
              {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={payerFilter} onValueChange={setPayerFilter}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Payer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">PFS + DIB</SelectItem>
              <SelectItem value="PFS">PFS only</SelectItem>
              <SelectItem value="DIB">DIB only</SelectItem>
            </SelectContent>
          </Select>
          {['owed', 'paid', 'all'].map((s) => (
            <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm" onClick={() => setStatusFilter(s)}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
          <Button variant="outline" size="sm" onClick={exportCsv} title="CSV of the rows in the current status filter">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {missingPayers.length > 0 && !loading && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          No contact named {missingPayers.join(' / ')} was found, so positions of that payer cannot be recognised.
          Expected names: &ldquo;Power Funding Solutions&rdquo; and &ldquo;DIB Capital INC&rdquo; / &ldquo;DIB Funding INC&rdquo;.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(totals).flatMap(([partner, byPayer]) => Object.entries(byPayer)
          .filter(([payer]) => payerFilter === 'all' || payer === payerFilter)
          .map(([payer, t]) => (
            <Card key={`${partner}:${payer}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-500">{payer} → {partner}</CardTitle>
                <Handshake className="h-4 w-4 text-indigo-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">{formatCurrency(t.open)}</div>
                <p className="text-xs text-slate-500 mt-1">open · {formatCurrency(t.paid)} paid · {formatCurrency(t.owed)} lifetime · {t.deals} deal{t.deals === 1 ? '' : 's'}</p>
              </CardContent>
            </Card>
          )))}
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
          ) : visible.length === 0 ? (
            <p className="text-center text-slate-400 py-12">No {statusFilter === 'all' ? '' : statusFilter} co-funded payables.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Deal</TableHead>
                  <TableHead>Merchant</TableHead>
                  <TableHead>Partner</TableHead>
                  <TableHead className="text-right">Partner %</TableHead>
                  <TableHead>Payer</TableHead>
                  <TableHead className="text-right">Payer %</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                  <TableHead className="text-right">Owed to partner</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Paid / Reference</TableHead>
                  <TableHead className="text-center w-14" title="Deal status: DEF = in Default, do not pay CM">Deal</TableHead>
                  <TableHead className="text-right w-36">Paid</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((e) => (
                  <TableRow key={e.key}>
                    <TableCell className="font-medium"><a className="text-blue-600 hover:underline" href={`/deals/${e.deal_id}`}>#{e.deal_id_number}</a></TableCell>
                    <TableCell className="text-slate-600 max-w-[220px] truncate">{e.deal_name}</TableCell>
                    <TableCell>{e.partner_name}</TableCell>
                    <TableCell className="text-right">{e.partner_pct}%</TableCell>
                    <TableCell><Badge variant="outline" className={e.payer === 'DIB' ? 'border-indigo-400 text-indigo-700' : 'border-blue-400 text-blue-700'}>{e.payer}</Badge></TableCell>
                    <TableCell className="text-right">{e.payer_pct}%</TableCell>
                    <TableCell className="text-right">{formatCurrency(e.commission)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(e.amount_owed)}</TableCell>
                    <TableCell><Badge variant={e.status === 'paid' ? 'default' : 'destructive'}>{e.status}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {e.paid_date ? String(e.paid_date).slice(0, 10) : ''}
                      {e.reference ? <span className="font-mono ml-2">{e.reference}</span> : null}
                      {e.note ? <div className="text-slate-400">{e.note}</div> : null}
                    </TableCell>
                    <TableCell className="text-center w-14">
                      {e.deal_defaulted ? (
                        <span className="inline-block rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white" title={`Deal status: ${e.deal_status}`}>DEF</span>
                      ) : <span className="text-slate-300" title={e.deal_status || ''}>—</span>}
                    </TableCell>
                    <TableCell className="text-right w-36">
                      {e.status === 'paid' ? (
                        <span className="text-green-600 text-xs inline-flex items-center justify-end gap-1">
                          <CheckCircle2 className="w-4 h-4" />{e.paid_date ? String(e.paid_date).slice(0, 10) : 'paid'}
                          <Button size="sm" variant="ghost" className="h-6 px-1" title="Undo" onClick={() => unmark(e)} disabled={busyKey === e.key}>
                            <RotateCcw className="w-3 h-3" />
                          </Button>
                        </span>
                      ) : (
                        <Button size="sm" onClick={() => openPay(e)} disabled={busyKey === e.key}>Mark Paid</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!payEntry} onOpenChange={(o) => { if (!o) setPayEntry(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {payEntry?.payer} paid {payEntry?.partner_name} — #{payEntry?.deal_id_number} · {formatCurrency(payEntry?.amount_owed || 0)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm text-slate-600">Paid date</label>
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
            </div>
            <div>
              <label className="text-sm text-slate-600">Wire / payment reference</label>
              <Input placeholder="reference number" value={payRef} onChange={(e) => setPayRef(e.target.value)} />
            </div>
            <div>
              <label className="text-sm text-slate-600">Note (optional)</label>
              <Input placeholder="note" value={payNote} onChange={(e) => setPayNote(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayEntry(null)}>Cancel</Button>
            <Button onClick={submitPay} disabled={busyKey === payEntry?.key}>
              {busyKey === payEntry?.key ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
              Confirm Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
